import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAppStore } from '../stores/useAppStore'
import { workOrderService, type WorkOrder } from '../services/workOrderService'
import { CalendarIcon, ClockIcon, MapPinIcon, UserIcon } from '@heroicons/react/24/outline'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'

const Schedule: React.FC = () => {
  const { user } = useAppStore()
  const [selectedWeek, setSelectedWeek] = useState(new Date().toISOString().split('T')[0])
  
  const technicianId = user?.id ? Number(user.id) : null
  const isValidTechnician = technicianId && technicianId > 0

  // Get current week dates
  const getWeekDates = (startDate: string) => {
    const start = new Date(startDate)
    const weekDates = []
    
    // Get Monday of the week
    const monday = new Date(start)
    monday.setDate(start.getDate() - start.getDay() + 1)
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday)
      date.setDate(monday.getDate() + i)
      weekDates.push(date)
    }
    
    return weekDates
  }

  const weekDates = getWeekDates(selectedWeek)
  
  const { 
    data: workOrders = [], 
    isLoading 
  } = useQuery<WorkOrder[]>({
    queryKey: ['schedule-workorders', technicianId, selectedWeek],
    queryFn: () => {
      if (!isValidTechnician) {
        throw new Error('Invalid technician ID')
      }
      return workOrderService.getTechnicianRunsheet(technicianId, selectedWeek)
    },
    enabled: !!isValidTechnician,
  })

  const getWorkOrdersForDate = (date: Date) => {
    return workOrders.filter(wo => {
      if (!wo.scheduledDate) return false
      const woDate = new Date(wo.scheduledDate)
      return woDate.toDateString() === date.toDateString()
    })
  }

  const isToday = (date: Date) => {
    const today = new Date()
    return date.toDateString() === today.toDateString()
  }

  const isWeekend = (date: Date) => {
    return date.getDay() === 0 || date.getDay() === 6
  }

  const getStatusBadgeVariant = (status: string) => {
    if (!status) return 'secondary'
    switch (status.toLowerCase()) {
      case 'scheduled': return 'outline'
      case 'in progress': return 'default'
      case 'completed': return 'secondary'
      case 'cancelled': return 'destructive'
      default: return 'secondary'
    }
  }

  if (!isValidTechnician) {
    return (
      <div className="container-mobile py-6">
        <Card className="max-w-2xl mx-auto">
          <CardHeader className="text-center">
            <CardTitle className="text-red-600">Invalid Technician</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600">
              Unable to identify technician. Please log out and log in again.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
            📅 Schedule
          </h1>
          <p className="text-gray-600 mt-1">
            View your weekly work schedule
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="weekSelector" className="text-sm font-medium">Week of:</Label>
            <Input
              id="weekSelector"
              type="date"
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              className="w-auto"
            />
          </div>
          
          {/* Week Summary Card */}
          <Card className="sm:w-auto">
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-4 text-sm">
                <div className="text-center">
                  <div className="font-semibold text-blue-600">{workOrders.length}</div>
                  <div className="text-gray-500">Total</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-green-600">
                    {workOrders.filter(wo => wo.status === 'Completed').length}
                  </div>
                  <div className="text-gray-500">Done</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-orange-600">
                    {workOrders.filter(wo => wo.status === 'Scheduled' || wo.status === 'In Progress').length}
                  </div>
                  <div className="text-gray-500">Pending</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Schedule Grid */}
      {isLoading ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-500">Loading schedule...</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4">
          {weekDates.map((date, index) => {
            const dayWorkOrders = getWorkOrdersForDate(date)
            const dayName = date.toLocaleDateString('en-US', { weekday: 'short' })
            const dayNumber = date.getDate()
            const monthName = date.toLocaleDateString('en-US', { month: 'short' })
            const todayFlag = isToday(date)
            const weekendFlag = isWeekend(date)
            
            return (
              <Card 
                key={index} 
                className={`
                  relative overflow-hidden transition-all
                  ${todayFlag ? 'ring-2 ring-blue-500 bg-blue-50' : ''}
                  ${weekendFlag ? 'bg-gray-50' : ''}
                `}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-sm font-medium text-gray-600">
                        {dayName}
                      </CardTitle>
                      <CardDescription className="text-lg font-bold text-gray-900">
                        {monthName} {dayNumber}
                      </CardDescription>
                    </div>
                    <Badge 
                      variant={dayWorkOrders.length > 0 ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {dayWorkOrders.length} job{dayWorkOrders.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  {todayFlag && (
                    <div className="absolute top-2 right-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    </div>
                  )}
                </CardHeader>
                
                <CardContent className="space-y-3">
                  {dayWorkOrders.length === 0 ? (
                    <div className="text-center py-6 text-gray-400">
                      <CalendarIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <span className="text-sm">No jobs scheduled</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {dayWorkOrders.map(workOrder => (
                        <div 
                          key={workOrder.workOrderID} 
                          className="bg-white rounded-lg border p-3 space-y-2 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <ClockIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span className="text-sm font-medium text-gray-900">
                                {workOrder.requestedTime || '9:00 AM'}
                              </span>
                            </div>
                            <Badge 
                              variant={getStatusBadgeVariant(workOrder.status || '')}
                              className="text-xs"
                            >
                              {workOrder.status}
                            </Badge>
                          </div>
                          
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <UserIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
                              <span className="text-xs font-medium text-gray-700 truncate">
                                {workOrder.clientName}
                              </span>
                            </div>
                            
                            <div className="flex items-start gap-2">
                              <MapPinIcon className="h-3 w-3 text-gray-400 flex-shrink-0 mt-0.5" />
                              <span className="text-xs text-gray-600 line-clamp-2">
                                {workOrder.propertyAddress}
                              </span>
                            </div>
                            
                            <div className="text-xs text-gray-500 truncate">
                              {workOrder.inspectionType}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default Schedule 